import {
  ChangeEvent,
  MouseEvent,
  cloneElement,
  useCallback,
  useEffect,
  useState
} from 'react';
import { Devices } from '../../service/Devices';
import { type HeaderProps, PROP_MAP } from './interface';

import { Avatar } from '../jdsavatar/generated';
import { Button } from '../jdsbutton/generated';
import { Icon } from '../jdsicon/generated';
import { Image } from '../jdsimage/generated';
import { Link } from '../jdslink/generated';
import { SidePanel } from '../jdssidepanel/generated';
import { SidePanelItem } from '../jdssidepanelitem/generated';
import { Text } from '../jdstext/generated';
import { debounce } from '@jds/utils';
import { modifyProps } from '../../generators/modifyProps';

const createIconLinks = (iconLinks: HeaderProps['iconLinks']) => {
  return iconLinks?.map((iconLink, i) => {
    const { icon, ...rest } = iconLink;
    return (
      <Link {...rest} key={iconLink.href || i}>
        <Button icon={icon} />
      </Link>
    );
  });
};

export const withLogic =
  (Component: React.ComponentType<HeaderProps>): React.FC<HeaderProps> =>
  (userProps: HeaderProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const links: any = props.links?.map((link, i: number) => ({
      ...link,
      active: props.activeLink === i,
      showSuffix: link.subLinks?.length,
      onHover: () => {
        if (Devices.isDesktop()) {
          setSelectedLink(i);
          setOpen(true);
        }
      }
    }));

    const [distance, setDistance] = useState({ x: 0, y: 0, width: 0 });
    const [selectedLink, setSelectedLink] = useState<number>();
    const [open, setOpen] = useState(false);
    const [internalSearchActive, setInternalSearchActive] = useState(
      props.variant === 'active_search'
    );
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const [searchValue, setSearchValue] = useState(
      props.searchConfig?.searchInput?.value
    );
    const [currentLinks, setCurrentLinks] = useState(links || []);
    const [sidePanelLinkIndex, setSidePanelLinkIndex] = useState(0);
    const [lastSidePanelItems, setLastSidePanelItems] = useState([links || []]);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelTitle, setSidePanelTitle] = useState('');

    const updatePosition = useCallback(
      debounce((element: HTMLDivElement | null) => {
        if (element) {
          const { x, y, width, height } = element.getBoundingClientRect();
          setDistance({ x, y: y + height, width });
        }
      }, 100),
      []
    );

    const ref = useCallback(
      (ref: HTMLDivElement) => {
        if (ref && (open || internalSearchActive)) {
          setElement(ref);
          updatePosition(ref);
        }
      },
      [updatePosition, open, internalSearchActive]
    );

    const currentSelectedLinkIndex =
      typeof selectedLink === 'number' ? selectedLink : -1;

    const megaMenuLinks = links?.[currentSelectedLinkIndex]?.subLinks?.map(
      (t) => ({
        ...t,
        links: t.subLinks
      })
    );

    const handleSearchActiveChange = (val: boolean) => {
      props?.onSearchStateChange?.(val ? 'open' : 'closed');
      setInternalSearchActive(val);
    };

    const listener = () => {
      setOpen(false);
      handleSearchActiveChange(false);
    };

    useEffect(() => {
      setInternalSearchActive(props.variant === 'active_search');
    }, [props.variant]);

    useEffect(() => {
      window.addEventListener('scroll', listener);
      return () => {
        window.removeEventListener('scroll', listener);
      };
    }, []);

    const LogoItem =
      props.logo === null ? undefined : props.logo?.props?.src ? (
        <Image src={props.logo?.props.src} />
      ) : (
        <Icon ic={props.logo || 'IcJioDot'} size='xl' color='secondary_50' />
      );

    const AvatarItem = props.avatar ? <Avatar {...props.avatar} /> : undefined;
    const { onFocus, onSuffixClick, onChange, onKeyDown, ...rest } =
      (props.searchConfig || {}).searchInput || {};

    useEffect(() => {
      if (open) {
        updatePosition(element);
      }
    }, [open, element]);

    return (
      <>
        <Component
          {...{
            ...props,
            ...rest,
            onSearchBoxSuffixClick: onSuffixClick,
            onSearchKeydown: onKeyDown || props.searchConfig?.onSearchKeyDown,
            _hasSearchResults: Boolean(props.searchConfig?.searchResults),
            suffix: <Link {...props.suffixLink}>{props.suffix}</Link>,
            prefixOnClick: () => {
              if (!Devices.isDesktop() && props.links?.length) {
                setSidePanelOpen(true);
              }
            },
            prefix:
              Devices.isDesktop() || !props.links?.length
                ? props.prefix
                : 'ic_burger_menu',
            logo: <Link {...props.logoLink}>{LogoItem}</Link>,
            avatar: <Link {...(props.avatarLink || {})}>{AvatarItem}</Link>,
            avatarOnClick: props.avatarOnClick || props.avatarLink,
            onSearchIconClick: () => handleSearchActiveChange(true),
            expandedSearch: internalSearchActive || props.expandedSearch,
            onSearchResultContainerClick: (ev: MouseEvent<HTMLDivElement>) =>
              ev.stopPropagation(),
            tabs: props.tabs
              ? cloneElement(props.tabs, {
                  appearance: 'tab-bar'
                })
              : null,
            onSearchResultClick: (ev: any) => {
              setSearchValue(ev.label);
              handleSearchActiveChange(false);
              props.searchConfig?.onSearchResultClick?.(ev);
              props.searchConfig?.searchResults?.onResultsItemClick?.(ev);
            },
            _platform: Devices.isDesktop() ? 'desktop' : 'mobile',
            links: Devices.isDesktop() ? links : undefined,
            query: searchValue,
            onSearchChange: (ev: ChangeEvent<HTMLInputElement>) => {
              setSearchValue(ev.target.value);
              props.searchConfig?.onSearchChange?.(ev.target.value);
              onChange?.(ev);
            },
            iconLinks: createIconLinks(props.iconLinks),
            _variant: internalSearchActive ? 'active_search' : 'default',
            onSearchFocus: (ev: any) => {
              handleSearchActiveChange(true);
              props.searchConfig?.onSearchFocus?.();
              onFocus?.(ev);
            },
            suffixOnClick: () => handleSearchActiveChange(false),
            megaMenuProps: {
              subLinks: megaMenuLinks,
              offsetX: distance.x,
              offsetY: distance.y,
              width: distance.width + distance.x,
              open
            },
            onMenuHoverEnd: () => setOpen(false),
            onSearchClose: () => handleSearchActiveChange(false),
            elementRef: ref
          }}
        />
        <SidePanel
          open={sidePanelOpen}
          onClose={() => setSidePanelOpen(false)}
          header={{
            titleBlock: {
              text: <Text color='primary-inverse'>{sidePanelTitle}</Text>
            },
            prefix: (
              <Button
                icon='ic_back'
                onClick={() => {
                  const currentIndex = sidePanelLinkIndex;
                  const actualIndex = currentIndex < 0 ? 0 : currentIndex;
                  const currentItems = [...lastSidePanelItems];
                  const lastItems = currentItems.splice(actualIndex, 1);
                  setSidePanelLinkIndex(actualIndex - 1);
                  if (lastItems?.[0]) {
                    setCurrentLinks(lastItems[0]);
                  }

                  setLastSidePanelItems(currentItems);
                }}
              />
            )
          }}
          maxWidth
        >
          {currentLinks?.map(
            (
              item: {
                type: any;
                showSuffix: any;
                prefix: any;
                title: any;
                ariaLabel: any;
                ariaDescribedby: any;
                itemSuffixAriaLabel: any;
                onSuffixClick: any;
                custom: any;
              },
              index: any
            ) => (
              <SidePanelItem
                key={index}
                type={item.type}
                showSuffix={item.showSuffix}
                prefix={item.prefix}
                title={item.title}
                ariaLabel={item.ariaLabel}
                ariaDescribedby={item.ariaDescribedby}
                itemSuffixAriaLabel={item.itemSuffixAriaLabel}
                onSuffixClick={() => {
                  const subLinks = item.subLinks?.map((l: any) => ({
                    ...l,
                    showSuffix: Boolean(l.subLinks?.length)
                  }));
                  if (subLinks) {
                    setCurrentLinks(subLinks);
                    setSidePanelLinkIndex(sidePanelLinkIndex + 1);
                    setLastSidePanelItems((link) => [...link, currentLinks]);
                    setSidePanelTitle(item.title || '');
                  }
                  item.onSuffixClick?.();
                }}
                custom={item.custom}
              />
            )
          )}
        </SidePanel>
      </>
    );
  };
export { HeaderProps };
